<script setup lang="ts">
import { TESTIMONIALS } from '../../data/testimonialsData'
import { ECMSType, ECMSPage, EHomePageSectionType } from '@/modules/adminModule/constants/pagesCMSconstants'

const googleRatingsStore = useGoogleRatingsStore()

const $t = useI18n().t

defineProps<{
	disableAos?: boolean
}>()
const ratingsForm = reactive({
	stars: '',
	ratings: '',
	ratingsCount: '',
	url: '',
})

await googleRatingsStore.fetchGoogleRating()

watch(
	() => googleRatingsStore.getGoogleRating,
	(v) => {
		if (v) {
			ratingsForm.stars = String(v.stars)
			ratingsForm.ratings = String(v.ratings)
			ratingsForm.ratingsCount = String(v.ratingsCount)
			ratingsForm.url = v.url
		}
	},
	{ immediate: true },
)

const testimonials = ref<
	| {
			[key: string]: any
	  }[]
	| undefined
>(TESTIMONIALS)

const { getLocaleLang } = usei18nService()

const pageSections = inject<Ref<IHomePageSectionModel[]>>('pageSections')!

if (!pageSections) {
	try {
		const type = await ApiService.fetchPageSectionByName(ECMSType.SUBPAGE, ECMSPage.HOME, EHomePageSectionType.TESTIMONIALS)
		if (type) testimonials.value = type?.translations.find((t) => t.language === getLocaleLang())?.content
	} catch (error) {
		console.log(error)
	}
}

watch(
	() => pageSections?.value,
	(newValue) => {
		if (newValue) {
			const section = newValue.find((section) => section.name === EHomePageSectionType.TESTIMONIALS)
			if (section) {
				testimonials.value = section.translations.find((t) => t.language === getLocaleLang())?.content
			}
		}
	},
	{ immediate: true, deep: true },
)
</script>

<template>
	<section class="testimonials">
		<div class="testimonials__container">
			<GlobalImage class="testimonials__image" src="testimonials-image.png" alt="testimonials-image" />
			<div class="testimonials__image-shadow"></div>
			<h2
				:data-aos="disableAos ? undefined : 'fade-up'"
				class="testimonials__title"
				v-html="detectNewline($t('modules.home_page_module.testimonials.opinions'))"></h2>
			<Swiper
				v-if="testimonials"
				:data-aos="disableAos ? undefined : 'fade-up'"
				class="testimonials__swiper"
				:slides-per-view="'auto'"
				:centered-slides="true"
				space-between="140"
				:loop="true"
				:loop-additional-slides="1"
				:watch-slides-progress="true"
				@after-init="
					(swiper) => {
						useTimeoutFn(() => {
							swiper.update()
						}, 400)
					}
				">
				<SwiperSlide v-for="(slide, idx) in testimonials" :key="idx" class="testimonials__swiper__slide">
					<TestimonialsItem
						:index="idx"
						:text="slide.translation_key_text || slide.text"
						:author="slide.translation_key_author || slide.author"
						:stars="slide.stars" />
				</SwiperSlide>
				<TestimonialsSwiperNav />
			</Swiper>
			<div class="testimonials__gradient testimonials__gradient--left"></div>
			<div class="testimonials__gradient testimonials__gradient--right"></div>
		</div>
		<div v-if="ratingsForm.stars && ratingsForm.stars !== ''" class="google-review">
			<div class="google-review__logo">
				<GlobalIcon name="google-icon" :height="16" />
			</div>
			<p :data-aos="disableAos ? undefined : 'fade-up'" class="google-review__text">
				{{ $t('modules.home_page_module.testimonials.assessment') }} {{ ratingsForm.ratings }}
				<span v-if="getLocaleLang() === 'en'"> OUT OF 5</span>
				{{ $t('modules.home_page_module.testimonials.of') }}
				<a :href="ratingsForm?.url" target="_blank" rel="nofollow" class="google-review__link">
					{{ ratingsForm.ratingsCount }} {{ $t('modules.home_page_module.testimonials.opinions_1') }}
				</a>
				<span v-if="getLocaleLang() === 'pl'"> GOOGLE </span>
			</p>
		</div>
	</section>
</template>

<style lang="scss">
@import './Testimonials';
</style>
